import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { formatMoney, localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { CheckIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import qs from 'qs';
import { Fragment, useState } from 'react';
import ContractCheckPeriodsTable from './components/ContractCheckPeriodsTable';
import EmployeeCard from './components/EmployeeCard';
import Navigation from './components/Navigation';

const ContractChecksView = () => {
    const { auth, filters, employments } = usePage().props;
    const [selectedRow, setSelectedRow] = useState(null);
    const confirm = useConfirmationStore(state => state.confirm);

    const handleRowSelected = (event, employmentId) => {
        if (
            !event.currentTarget.contains(event.target) ||
            event.target.closest(
                'button, a, input, label, select, textarea, [role="checkbox"]'
            )
        ) {
            return;
        }

        setSelectedRow(prev => (prev === employmentId ? null : employmentId));
    };

    const complete = contractCheckId =>
        confirm({
            description:
                'Weet u zeker dat u de contractcontrole wilt voltooien?',
            onConfirm: close => {
                router.patch(
                    `/customers/${auth?.user?.selected_customer_id}/services/ww-check/contract-check/${contractCheckId}`,
                    {
                        status: 3, // EmploymentContractStatus::ContractChecked
                    },
                    {
                        preserveScroll: true,
                        only: [
                            'employments',
                            'business_rules',
                            'contract_checks',
                            'savings',
                        ],
                    }
                );

                close();
            },
        });

    return (
        <Container fluid>
            <Navigation reload={['employments']} />

            <Segment>
                <SegmentHeader
                    header={`Contract check (${employments?.meta?.total || 0})`}
                >
                    <Button
                        as='a'
                        target='_blank'
                        rel='noopener noreferrer'
                        href={`/customers/${auth?.user?.selected_customer_id}/services/ww-check/contract-check/exports?${qs.stringify(
                            {
                                entity_id: filters?.entity_id || undefined,
                            }
                        )}`}
                    >
                        Exporteren
                    </Button>
                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionMarkCircleIcon className='size-5' />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                De enige manier om te bevestigen of de gegevens
                                daadwerkelijk onjuist zijn, is door deze te
                                controleren aan de hand van het contract van de
                                werknemer (zowel fysiek als digitaal). Aangezien
                                dit een tijdrovend proces is, zowel voor ons als
                                voor de klant, gebruiken we de regels en
                                signalen om te identificeren welke werknemers
                                mogelijk besparingen kunnen opleveren. Hoewel
                                het vermogen om contractcontroles uit te voeren
                                voor een subset van werknemers bij een bedrijf
                                handig is (vooral voor grotere klanten), is het
                                ultieme doel om alle contracten van werknemers
                                die momenteel de hoge premie betalen te
                                controleren.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['employments']} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                <TableHeaderCell>
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell collapsing>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Maximaal Besparingen
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Besparingen
                                </TableHeaderCell>
                                <TableHeaderCell content='' textAlign='right'>
                                    Acties
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {employments?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={6} />
                            )}

                            {employments?.data?.map(employment => (
                                <Fragment key={employment.id}>
                                    <TableRow
                                        onClick={event =>
                                            handleRowSelected(
                                                event,
                                                employment.id
                                            )
                                        }
                                    >
                                        <TableCell>
                                            <EmployeeCard
                                                employment={employment}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {localDate(employment.starts_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span>
                                                        {localDate(
                                                            employment.ends_at
                                                        ) || '-'}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {employment.duration}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(
                                                employment.max_savings || 0
                                            )}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(
                                                employment.ww_contract_check
                                                    .savings || 0
                                            )}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant='primary'
                                                        onClick={() =>
                                                            complete(
                                                                employment
                                                                    .ww_contract_check
                                                                    .id
                                                            )
                                                        }
                                                    >
                                                        <CheckIcon className='size-4' />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    Volledige contract check.
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    {selectedRow === employment.id && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <ContractCheckPeriodsTable
                                                    data={
                                                        employment.employment_periods
                                                    }
                                                    contractCheck={
                                                        employment.ww_contract_check
                                                    }
                                                    year={filters?.year}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={employments?.meta}
                        only={['employments']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default ContractChecksView;
